import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Upload, CheckCircle, Loader, FileText, Volume2, Users, Home, Scale } from 'lucide-react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';

interface Property {
  id: number;
  address: string;
  leaseId: number;
}

const COMPLAINT_CATEGORIES = [
  { value: 'NOISE', label: 'Noise', icon: 'volume', description: 'Excessive noise from neighbours or surroundings' },
  { value: 'NEIGHBOUR', label: 'Neighbour Issues', icon: 'users', description: 'Disputes or issues with neighbours' },
  { value: 'PROPERTY_DEFECT', label: 'Property Defects', icon: 'home', description: 'Structural or maintenance issues' },
  { value: 'AGENT_CONDUCT', label: 'Agent Conduct', icon: 'scale', description: 'Concerns about agent behaviour or actions' }
];

const SEVERITY_LEVELS = [
  { value: 'LOW', label: 'Low', color: '#49c5b6', description: 'Minor issue, not urgent' },
  { value: 'MEDIUM', label: 'Medium', color: '#F59E0B', description: 'Significant issue, needs attention' },
  { value: 'HIGH', label: 'High', color: '#DF6C4F', description: 'Serious issue, urgent response needed' }
];

const getCategoryIcon = (iconName: string) => {
  switch (iconName) {
    case 'volume': return <Volume2 className="w-6 h-6" />;
    case 'users': return <Users className="w-6 h-6" />;
    case 'home': return <Home className="w-6 h-6" />;
    case 'scale': return <Scale className="w-6 h-6" />;
    default: return <AlertTriangle className="w-6 h-6" />;
  }
};

export default function FileComplaint() {
  const navigate = useNavigate();

  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<number | null>(null);

  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('MEDIUM');
  const [isUrgent, setIsUrgent] = useState(false);

  const [incidentDate, setIncidentDate] = useState('');
  const [incidentTime, setIncidentTime] = useState('');
  const [isOngoing, setIsOngoing] = useState(false);
  const [policeReportNumber, setPoliceReportNumber] = useState('');

  const [evidence, setEvidence] = useState<File[]>([]);
  const [evidencePreviews, setEvidencePreviews] = useState<string[]>([]);
  const [evidenceDescriptions, setEvidenceDescriptions] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [complaintId, setComplaintId] = useState<number | null>(null);

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const response = await api.get<{leases: any[]}>('/renter/onboarding/available-leases');
      const propertyList = response.leases?.map(lease => ({
        id: lease.propertyId,
        address: lease.propertyAddress,
        leaseId: lease.id
      })) || [];

      setProperties(propertyList);
      if (propertyList.length > 0) {
        setSelectedProperty(propertyList[0].leaseId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleEvidenceSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f =>
      f.type.startsWith('image/') ||
      f.type.startsWith('video/') ||
      f.type.startsWith('audio/') ||
      f.type === 'application/pdf'
    );

    if (validFiles.length + evidence.length > 20) {
      alert('Maximum 20 evidence files allowed');
      return;
    }

    setEvidence([...evidence, ...validFiles]);
    setEvidenceDescriptions([...evidenceDescriptions, ...Array(validFiles.length).fill('')]);

    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setEvidencePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      } else {
        // For non-images, use a placeholder
        setEvidencePreviews(prev => [...prev, '']);
      }
    });
  };

  const removeEvidence = (index: number) => {
    setEvidence(evidence.filter((_, i) => i !== index));
    setEvidencePreviews(evidencePreviews.filter((_, i) => i !== index));
    setEvidenceDescriptions(evidenceDescriptions.filter((_, i) => i !== index));
  };

  const updateEvidenceDescription = (index: number, desc: string) => {
    const newDescriptions = [...evidenceDescriptions];
    newDescriptions[index] = desc;
    setEvidenceDescriptions(newDescriptions);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProperty) {
      setError('Please select a property');
      return;
    }

    if (!category) {
      setError('Please select a complaint category');
      return;
    }

    if (subject.length < 5) {
      setError('Please provide a subject (at least 5 characters)');
      return;
    }

    if (description.length < 20) {
      setError('Please provide a detailed description (at least 20 characters)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Upload evidence files (simulated - in production, upload to S3)
      const evidenceData: any[] = [];
      for (let i = 0; i < evidence.length; i++) {
        const file = evidence[i];
        const fileUrl = `evidence/${Date.now()}_${file.name}`;

        let evidenceType = 'DOCUMENT';
        if (file.type.startsWith('image/')) evidenceType = 'PHOTO';
        else if (file.type.startsWith('video/')) evidenceType = 'VIDEO';
        else if (file.type.startsWith('audio/')) evidenceType = 'AUDIO';

        evidenceData.push({
          fileUrl,
          fileType: file.type,
          fileName: file.name,
          evidenceType,
          description: evidenceDescriptions[i] || '',
          capturedAt: incidentDate ? new Date(incidentDate).toISOString() : new Date().toISOString()
        });
      }

      const payload: any = {
        leaseId: selectedProperty,
        category,
        subject,
        description,
        severity,
        isUrgent,
        evidence: evidenceData
      };

      if (incidentDate) {
        payload.incidentDate = new Date(incidentDate).toISOString();
      }
      if (incidentTime) {
        payload.incidentTime = incidentTime;
      }
      if (isOngoing) {
        payload.isOngoing = true;
      }
      if (policeReportNumber) {
        payload.policeReportNumber = policeReportNumber;
      }

      const response = await api.post<{complaintId: number}>('/complaints', payload);

      setComplaintId(response.complaintId);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit complaint');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#0C4432' }}></div>
      </div>
    );
  }

  if (success && complaintId) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#49c5b6' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#0C4432' }}>Complaint Filed</h2>
          <p className="mb-4" style={{ color: '#666' }}>
            Your complaint has been submitted and logged for review.
          </p>
          <div className="bg-gray-50 rounded-md p-4 mb-6">
            <p className="text-sm" style={{ color: '#666' }}>Reference Number</p>
            <p className="text-2xl font-bold" style={{ color: '#0C4432' }}>#{complaintId}</p>
          </div>
          <div className="text-left mb-6 p-4 rounded-md" style={{ backgroundColor: '#FFF3CD' }}>
            <p className="text-sm font-medium mb-2" style={{ color: '#856404' }}>Important:</p>
            <ul className="text-xs space-y-1" style={{ color: '#856404' }}>
              <li>• Your complaint is now part of an official record</li>
              <li>• All evidence is preserved for potential tribunal use</li>
              <li>• You'll receive updates as this is reviewed</li>
              <li>• Keep this reference number for your records</li>
            </ul>
          </div>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-2 px-4 rounded-md text-white font-medium"
              style={{ backgroundColor: '#0C4432' }}
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => navigate(`/complaints/${complaintId}`)}
              className="w-full py-2 px-4 rounded-md border font-medium"
              style={{ borderColor: '#49c5b6', color: '#49c5b6' }}
            >
              View Complaint
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: '#F8F8F8' }}>
      <div className="max-w-3xl mx-auto">
        <PageHeader
          title="File a Complaint"
          subtitle="Report an issue with your property, neighbours, or agent"
          icon={<AlertTriangle className="w-8 h-8" />}
          backPath="/dashboard"
        />

        {error && (
          <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#FEE', color: '#DF6C4F' }}>
            {error}
          </div>
        )}

        <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: '#E8F5F3' }}>
          <p className="text-sm" style={{ color: '#0C4432' }}>
            <strong>Notice:</strong> This complaint will be logged as an official record and may be used as evidence in tribunals or disputes.
            Please provide accurate and detailed information.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Property Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
              Property *
            </label>
            <select
              value={selectedProperty || ''}
              onChange={(e) => setSelectedProperty(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="">Select a property</option>
              {properties.map((property) => (
                <option key={property.leaseId} value={property.leaseId}>
                  {property.address}
                </option>
              ))}
            </select>
          </div>

          {/* Category Selection */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-3" style={{ color: '#222' }}>
              Complaint Category *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {COMPLAINT_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`p-4 border-2 rounded-lg text-left transition-colors ${category === cat.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-primary">{getCategoryIcon(cat.icon)}</span>
                    <div>
                      <p className="font-medium text-gray-900">{cat.label}</p>
                      <p className="text-xs mt-1 text-gray-500">{cat.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Subject and Description */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                Subject *
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of the issue..."
                className="w-full px-3 py-2 border rounded-md"
                minLength={5}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                Detailed Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide a detailed description of the issue, including what happened, when, and any relevant context..."
                className="w-full px-3 py-2 border rounded-md"
                rows={6}
                minLength={20}
                required
              />
              <p className="text-xs mt-1" style={{ color: '#666' }}>
                {description.length} / 20 minimum characters
              </p>
            </div>
          </div>

          {/* Severity and Urgency */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-3" style={{ color: '#222' }}>
              Severity Level *
            </label>
            <div className="space-y-2 mb-4">
              {SEVERITY_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${severity === level.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                >
                  <input
                    type="radio"
                    name="severity"
                    value={level.value}
                    checked={severity === level.value}
                    onChange={(e) => setSeverity(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: level.color }}>{level.label}</p>
                    <p className="text-sm" style={{ color: '#666' }}>{level.description}</p>
                  </div>
                </label>
              ))}
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isUrgent}
                onChange={(e) => setIsUrgent(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium" style={{ color: '#DC2626' }}>
                This is an urgent matter requiring immediate attention
              </span>
            </label>
          </div>

          {/* Incident Details */}
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h3 className="font-semibold" style={{ color: '#0C4432' }}>Incident Details</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                  Incident Date
                </label>
                <input
                  type="date"
                  value={incidentDate}
                  onChange={(e) => setIncidentDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                  Incident Time
                </label>
                <input
                  type="time"
                  value={incidentTime}
                  onChange={(e) => setIncidentTime(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isOngoing}
                onChange={(e) => setIsOngoing(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm" style={{ color: '#222' }}>
                This is an ongoing issue
              </span>
            </label>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#222' }}>
                Police Report Number (if applicable)
              </label>
              <input
                type="text"
                value={policeReportNumber}
                onChange={(e) => setPoliceReportNumber(e.target.value)}
                placeholder="e.g., INC123456"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          {/* Evidence Upload */}
          <div className="bg-white rounded-lg shadow p-6">
            <label className="block text-sm font-medium mb-3" style={{ color: '#222' }}>
              Evidence (Photos, Videos, Documents, Audio)
            </label>
            <div className="border-2 border-dashed rounded-lg p-6 text-center mb-4">
              <input
                type="file"
                id="evidence-upload"
                accept="image/*,video/*,audio/*,.pdf"
                multiple
                onChange={handleEvidenceSelect}
                className="hidden"
              />
              <label htmlFor="evidence-upload" className="cursor-pointer flex flex-col items-center">
                <Upload className="w-12 h-12 mb-2" style={{ color: '#666' }} />
                <p className="font-medium" style={{ color: '#0C4432' }}>
                  Upload Evidence Files
                </p>
                <p className="text-sm" style={{ color: '#666' }}>
                  Photos, videos, audio recordings, or documents (up to 20 files)
                </p>
              </label>
            </div>

            {evidence.length > 0 && (
              <div className="space-y-3">
                {evidence.map((file, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-md">
                    {evidencePreviews[index] ? (
                      <img src={evidencePreviews[index]} alt={`Evidence ${index + 1}`} className="w-16 h-16 object-cover rounded" />
                    ) : (
                      <FileText className="w-16 h-16 p-2" style={{ color: '#666' }} />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: '#222' }}>{file.name}</p>
                      <input
                        type="text"
                        value={evidenceDescriptions[index]}
                        onChange={(e) => updateEvidenceDescription(index, e.target.value)}
                        placeholder="Describe this evidence..."
                        className="w-full mt-1 px-2 py-1 text-xs border rounded"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEvidence(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 rounded-md text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#DF6C4F' }}
          >
            {submitting ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Submitting Complaint...
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5" />
                File Complaint
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

